// phone-country — derive ISO-2 country + flag emoji from a phone country code.
//
// Aviation broker workflows constantly involve UAE (+971), Egypt (+20),
// Indonesia (+62), Turkey (+90), UK (+44), Malaysia (+60), Singapore (+65),
// India (+91), and similar markets. This util gives a fast at-a-glance
// country read from a phone number with no external dependency.
//
// Strategy: longest-prefix match against E.164 country calling codes.
// Returns null when the phone is empty, doesn't start with a digit/+, or
// the prefix isn't recognized.

interface CountryInfo {
  iso2: string;
  name: string;
  flag: string; // emoji
}

// Most-common E.164 codes APD will encounter, longest-first for greedy match.
// (Full ITU list is ~250 entries; this covers ~95% of real-world traffic.)
const CODES: Array<[string, CountryInfo]> = [
  // 4-digit (none in standard ITU — placeholder for future)
  // 3-digit
  ['1242', { iso2: 'BS', name: 'Bahamas', flag: '🇧🇸' }],
  ['1246', { iso2: 'BB', name: 'Barbados', flag: '🇧🇧' }],
  ['1268', { iso2: 'AG', name: 'Antigua and Barbuda', flag: '🇦🇬' }],
  ['1284', { iso2: 'VG', name: 'British Virgin Islands', flag: '🇻🇬' }],
  ['1340', { iso2: 'VI', name: 'US Virgin Islands', flag: '🇻🇮' }],
  ['1345', { iso2: 'KY', name: 'Cayman Islands', flag: '🇰🇾' }],
  ['1441', { iso2: 'BM', name: 'Bermuda', flag: '🇧🇲' }],
  ['1473', { iso2: 'GD', name: 'Grenada', flag: '🇬🇩' }],
  ['1649', { iso2: 'TC', name: 'Turks and Caicos', flag: '🇹🇨' }],
  ['1664', { iso2: 'MS', name: 'Montserrat', flag: '🇲🇸' }],
  ['1670', { iso2: 'MP', name: 'Northern Mariana Islands', flag: '🇲🇵' }],
  ['1671', { iso2: 'GU', name: 'Guam', flag: '🇬🇺' }],
  ['1684', { iso2: 'AS', name: 'American Samoa', flag: '🇦🇸' }],
  ['1721', { iso2: 'SX', name: 'Sint Maarten', flag: '🇸🇽' }],
  ['1758', { iso2: 'LC', name: 'Saint Lucia', flag: '🇱🇨' }],
  ['1767', { iso2: 'DM', name: 'Dominica', flag: '🇩🇲' }],
  ['1784', { iso2: 'VC', name: 'St Vincent and the Grenadines', flag: '🇻🇨' }],
  ['1787', { iso2: 'PR', name: 'Puerto Rico', flag: '🇵🇷' }],
  ['1809', { iso2: 'DO', name: 'Dominican Republic', flag: '🇩🇴' }],
  ['1829', { iso2: 'DO', name: 'Dominican Republic', flag: '🇩🇴' }],
  ['1849', { iso2: 'DO', name: 'Dominican Republic', flag: '🇩🇴' }],
  ['1868', { iso2: 'TT', name: 'Trinidad and Tobago', flag: '🇹🇹' }],
  ['1869', { iso2: 'KN', name: 'Saint Kitts and Nevis', flag: '🇰🇳' }],
  ['1876', { iso2: 'JM', name: 'Jamaica', flag: '🇯🇲' }],
  ['1939', { iso2: 'PR', name: 'Puerto Rico', flag: '🇵🇷' }],
  ['212', { iso2: 'MA', name: 'Morocco', flag: '🇲🇦' }],
  ['213', { iso2: 'DZ', name: 'Algeria', flag: '🇩🇿' }],
  ['216', { iso2: 'TN', name: 'Tunisia', flag: '🇹🇳' }],
  ['218', { iso2: 'LY', name: 'Libya', flag: '🇱🇾' }],
  ['220', { iso2: 'GM', name: 'Gambia', flag: '🇬🇲' }],
  ['221', { iso2: 'SN', name: 'Senegal', flag: '🇸🇳' }],
  ['222', { iso2: 'MR', name: 'Mauritania', flag: '🇲🇷' }],
  ['223', { iso2: 'ML', name: 'Mali', flag: '🇲🇱' }],
  ['224', { iso2: 'GN', name: 'Guinea', flag: '🇬🇳' }],
  ['225', { iso2: 'CI', name: "Côte d'Ivoire", flag: '🇨🇮' }],
  ['226', { iso2: 'BF', name: 'Burkina Faso', flag: '🇧🇫' }],
  ['227', { iso2: 'NE', name: 'Niger', flag: '🇳🇪' }],
  ['228', { iso2: 'TG', name: 'Togo', flag: '🇹🇬' }],
  ['229', { iso2: 'BJ', name: 'Benin', flag: '🇧🇯' }],
  ['230', { iso2: 'MU', name: 'Mauritius', flag: '🇲🇺' }],
  ['231', { iso2: 'LR', name: 'Liberia', flag: '🇱🇷' }],
  ['232', { iso2: 'SL', name: 'Sierra Leone', flag: '🇸🇱' }],
  ['233', { iso2: 'GH', name: 'Ghana', flag: '🇬🇭' }],
  ['234', { iso2: 'NG', name: 'Nigeria', flag: '🇳🇬' }],
  ['235', { iso2: 'TD', name: 'Chad', flag: '🇹🇩' }],
  ['236', { iso2: 'CF', name: 'Central African Republic', flag: '🇨🇫' }],
  ['237', { iso2: 'CM', name: 'Cameroon', flag: '🇨🇲' }],
  ['238', { iso2: 'CV', name: 'Cape Verde', flag: '🇨🇻' }],
  ['239', { iso2: 'ST', name: 'São Tomé and Príncipe', flag: '🇸🇹' }],
  ['240', { iso2: 'GQ', name: 'Equatorial Guinea', flag: '🇬🇶' }],
  ['241', { iso2: 'GA', name: 'Gabon', flag: '🇬🇦' }],
  ['242', { iso2: 'CG', name: 'Republic of Congo', flag: '🇨🇬' }],
  ['243', { iso2: 'CD', name: 'DR Congo', flag: '🇨🇩' }],
  ['244', { iso2: 'AO', name: 'Angola', flag: '🇦🇴' }],
  ['245', { iso2: 'GW', name: 'Guinea-Bissau', flag: '🇬🇼' }],
  ['248', { iso2: 'SC', name: 'Seychelles', flag: '🇸🇨' }],
  ['249', { iso2: 'SD', name: 'Sudan', flag: '🇸🇩' }],
  ['250', { iso2: 'RW', name: 'Rwanda', flag: '🇷🇼' }],
  ['251', { iso2: 'ET', name: 'Ethiopia', flag: '🇪🇹' }],
  ['252', { iso2: 'SO', name: 'Somalia', flag: '🇸🇴' }],
  ['253', { iso2: 'DJ', name: 'Djibouti', flag: '🇩🇯' }],
  ['254', { iso2: 'KE', name: 'Kenya', flag: '🇰🇪' }],
  ['255', { iso2: 'TZ', name: 'Tanzania', flag: '🇹🇿' }],
  ['256', { iso2: 'UG', name: 'Uganda', flag: '🇺🇬' }],
  ['257', { iso2: 'BI', name: 'Burundi', flag: '🇧🇮' }],
  ['258', { iso2: 'MZ', name: 'Mozambique', flag: '🇲🇿' }],
  ['260', { iso2: 'ZM', name: 'Zambia', flag: '🇿🇲' }],
  ['261', { iso2: 'MG', name: 'Madagascar', flag: '🇲🇬' }],
  ['263', { iso2: 'ZW', name: 'Zimbabwe', flag: '🇿🇼' }],
  ['264', { iso2: 'NA', name: 'Namibia', flag: '🇳🇦' }],
  ['265', { iso2: 'MW', name: 'Malawi', flag: '🇲🇼' }],
  ['266', { iso2: 'LS', name: 'Lesotho', flag: '🇱🇸' }],
  ['267', { iso2: 'BW', name: 'Botswana', flag: '🇧🇼' }],
  ['351', { iso2: 'PT', name: 'Portugal', flag: '🇵🇹' }],
  ['352', { iso2: 'LU', name: 'Luxembourg', flag: '🇱🇺' }],
  ['353', { iso2: 'IE', name: 'Ireland', flag: '🇮🇪' }],
  ['354', { iso2: 'IS', name: 'Iceland', flag: '🇮🇸' }],
  ['355', { iso2: 'AL', name: 'Albania', flag: '🇦🇱' }],
  ['356', { iso2: 'MT', name: 'Malta', flag: '🇲🇹' }],
  ['357', { iso2: 'CY', name: 'Cyprus', flag: '🇨🇾' }],
  ['358', { iso2: 'FI', name: 'Finland', flag: '🇫🇮' }],
  ['359', { iso2: 'BG', name: 'Bulgaria', flag: '🇧🇬' }],
  ['370', { iso2: 'LT', name: 'Lithuania', flag: '🇱🇹' }],
  ['371', { iso2: 'LV', name: 'Latvia', flag: '🇱🇻' }],
  ['372', { iso2: 'EE', name: 'Estonia', flag: '🇪🇪' }],
  ['373', { iso2: 'MD', name: 'Moldova', flag: '🇲🇩' }],
  ['374', { iso2: 'AM', name: 'Armenia', flag: '🇦🇲' }],
  ['375', { iso2: 'BY', name: 'Belarus', flag: '🇧🇾' }],
  ['376', { iso2: 'AD', name: 'Andorra', flag: '🇦🇩' }],
  ['377', { iso2: 'MC', name: 'Monaco', flag: '🇲🇨' }],
  ['380', { iso2: 'UA', name: 'Ukraine', flag: '🇺🇦' }],
  ['381', { iso2: 'RS', name: 'Serbia', flag: '🇷🇸' }],
  ['382', { iso2: 'ME', name: 'Montenegro', flag: '🇲🇪' }],
  ['383', { iso2: 'XK', name: 'Kosovo', flag: '🇽🇰' }],
  ['385', { iso2: 'HR', name: 'Croatia', flag: '🇭🇷' }],
  ['386', { iso2: 'SI', name: 'Slovenia', flag: '🇸🇮' }],
  ['387', { iso2: 'BA', name: 'Bosnia and Herzegovina', flag: '🇧🇦' }],
  ['389', { iso2: 'MK', name: 'North Macedonia', flag: '🇲🇰' }],
  ['420', { iso2: 'CZ', name: 'Czech Republic', flag: '🇨🇿' }],
  ['421', { iso2: 'SK', name: 'Slovakia', flag: '🇸🇰' }],
  ['423', { iso2: 'LI', name: 'Liechtenstein', flag: '🇱🇮' }],
  ['501', { iso2: 'BZ', name: 'Belize', flag: '🇧🇿' }],
  ['502', { iso2: 'GT', name: 'Guatemala', flag: '🇬🇹' }],
  ['503', { iso2: 'SV', name: 'El Salvador', flag: '🇸🇻' }],
  ['504', { iso2: 'HN', name: 'Honduras', flag: '🇭🇳' }],
  ['505', { iso2: 'NI', name: 'Nicaragua', flag: '🇳🇮' }],
  ['506', { iso2: 'CR', name: 'Costa Rica', flag: '🇨🇷' }],
  ['507', { iso2: 'PA', name: 'Panama', flag: '🇵🇦' }],
  ['509', { iso2: 'HT', name: 'Haiti', flag: '🇭🇹' }],
  ['590', { iso2: 'GP', name: 'Guadeloupe', flag: '🇬🇵' }],
  ['591', { iso2: 'BO', name: 'Bolivia', flag: '🇧🇴' }],
  ['592', { iso2: 'GY', name: 'Guyana', flag: '🇬🇾' }],
  ['593', { iso2: 'EC', name: 'Ecuador', flag: '🇪🇨' }],
  ['595', { iso2: 'PY', name: 'Paraguay', flag: '🇵🇾' }],
  ['597', { iso2: 'SR', name: 'Suriname', flag: '🇸🇷' }],
  ['598', { iso2: 'UY', name: 'Uruguay', flag: '🇺🇾' }],
  ['670', { iso2: 'TL', name: 'Timor-Leste', flag: '🇹🇱' }],
  ['673', { iso2: 'BN', name: 'Brunei', flag: '🇧🇳' }],
  ['675', { iso2: 'PG', name: 'Papua New Guinea', flag: '🇵🇬' }],
  ['676', { iso2: 'TO', name: 'Tonga', flag: '🇹🇴' }],
  ['679', { iso2: 'FJ', name: 'Fiji', flag: '🇫🇯' }],
  ['680', { iso2: 'PW', name: 'Palau', flag: '🇵🇼' }],
  ['685', { iso2: 'WS', name: 'Samoa', flag: '🇼🇸' }],
  ['691', { iso2: 'FM', name: 'Micronesia', flag: '🇫🇲' }],
  ['692', { iso2: 'MH', name: 'Marshall Islands', flag: '🇲🇭' }],
  ['850', { iso2: 'KP', name: 'North Korea', flag: '🇰🇵' }],
  ['852', { iso2: 'HK', name: 'Hong Kong', flag: '🇭🇰' }],
  ['853', { iso2: 'MO', name: 'Macau', flag: '🇲🇴' }],
  ['855', { iso2: 'KH', name: 'Cambodia', flag: '🇰🇭' }],
  ['856', { iso2: 'LA', name: 'Laos', flag: '🇱🇦' }],
  ['880', { iso2: 'BD', name: 'Bangladesh', flag: '🇧🇩' }],
  ['886', { iso2: 'TW', name: 'Taiwan', flag: '🇹🇼' }],
  ['960', { iso2: 'MV', name: 'Maldives', flag: '🇲🇻' }],
  ['961', { iso2: 'LB', name: 'Lebanon', flag: '🇱🇧' }],
  ['962', { iso2: 'JO', name: 'Jordan', flag: '🇯🇴' }],
  ['963', { iso2: 'SY', name: 'Syria', flag: '🇸🇾' }],
  ['964', { iso2: 'IQ', name: 'Iraq', flag: '🇮🇶' }],
  ['965', { iso2: 'KW', name: 'Kuwait', flag: '🇰🇼' }],
  ['966', { iso2: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' }],
  ['967', { iso2: 'YE', name: 'Yemen', flag: '🇾🇪' }],
  ['968', { iso2: 'OM', name: 'Oman', flag: '🇴🇲' }],
  ['970', { iso2: 'PS', name: 'Palestine', flag: '🇵🇸' }],
  ['971', { iso2: 'AE', name: 'UAE', flag: '🇦🇪' }],
  ['972', { iso2: 'IL', name: 'Israel', flag: '🇮🇱' }],
  ['973', { iso2: 'BH', name: 'Bahrain', flag: '🇧🇭' }],
  ['974', { iso2: 'QA', name: 'Qatar', flag: '🇶🇦' }],
  ['975', { iso2: 'BT', name: 'Bhutan', flag: '🇧🇹' }],
  ['976', { iso2: 'MN', name: 'Mongolia', flag: '🇲🇳' }],
  ['977', { iso2: 'NP', name: 'Nepal', flag: '🇳🇵' }],
  ['992', { iso2: 'TJ', name: 'Tajikistan', flag: '🇹🇯' }],
  ['993', { iso2: 'TM', name: 'Turkmenistan', flag: '🇹🇲' }],
  ['994', { iso2: 'AZ', name: 'Azerbaijan', flag: '🇦🇿' }],
  ['995', { iso2: 'GE', name: 'Georgia', flag: '🇬🇪' }],
  ['996', { iso2: 'KG', name: 'Kyrgyzstan', flag: '🇰🇬' }],
  ['998', { iso2: 'UZ', name: 'Uzbekistan', flag: '🇺🇿' }],
  // 2-digit
  ['20', { iso2: 'EG', name: 'Egypt', flag: '🇪🇬' }],
  ['27', { iso2: 'ZA', name: 'South Africa', flag: '🇿🇦' }],
  ['30', { iso2: 'GR', name: 'Greece', flag: '🇬🇷' }],
  ['31', { iso2: 'NL', name: 'Netherlands', flag: '🇳🇱' }],
  ['32', { iso2: 'BE', name: 'Belgium', flag: '🇧🇪' }],
  ['33', { iso2: 'FR', name: 'France', flag: '🇫🇷' }],
  ['34', { iso2: 'ES', name: 'Spain', flag: '🇪🇸' }],
  ['36', { iso2: 'HU', name: 'Hungary', flag: '🇭🇺' }],
  ['39', { iso2: 'IT', name: 'Italy', flag: '🇮🇹' }],
  ['40', { iso2: 'RO', name: 'Romania', flag: '🇷🇴' }],
  ['41', { iso2: 'CH', name: 'Switzerland', flag: '🇨🇭' }],
  ['43', { iso2: 'AT', name: 'Austria', flag: '🇦🇹' }],
  ['44', { iso2: 'GB', name: 'United Kingdom', flag: '🇬🇧' }],
  ['45', { iso2: 'DK', name: 'Denmark', flag: '🇩🇰' }],
  ['46', { iso2: 'SE', name: 'Sweden', flag: '🇸🇪' }],
  ['47', { iso2: 'NO', name: 'Norway', flag: '🇳🇴' }],
  ['48', { iso2: 'PL', name: 'Poland', flag: '🇵🇱' }],
  ['49', { iso2: 'DE', name: 'Germany', flag: '🇩🇪' }],
  ['51', { iso2: 'PE', name: 'Peru', flag: '🇵🇪' }],
  ['52', { iso2: 'MX', name: 'Mexico', flag: '🇲🇽' }],
  ['53', { iso2: 'CU', name: 'Cuba', flag: '🇨🇺' }],
  ['54', { iso2: 'AR', name: 'Argentina', flag: '🇦🇷' }],
  ['55', { iso2: 'BR', name: 'Brazil', flag: '🇧🇷' }],
  ['56', { iso2: 'CL', name: 'Chile', flag: '🇨🇱' }],
  ['57', { iso2: 'CO', name: 'Colombia', flag: '🇨🇴' }],
  ['58', { iso2: 'VE', name: 'Venezuela', flag: '🇻🇪' }],
  ['60', { iso2: 'MY', name: 'Malaysia', flag: '🇲🇾' }],
  ['61', { iso2: 'AU', name: 'Australia', flag: '🇦🇺' }],
  ['62', { iso2: 'ID', name: 'Indonesia', flag: '🇮🇩' }],
  ['63', { iso2: 'PH', name: 'Philippines', flag: '🇵🇭' }],
  ['64', { iso2: 'NZ', name: 'New Zealand', flag: '🇳🇿' }],
  ['65', { iso2: 'SG', name: 'Singapore', flag: '🇸🇬' }],
  ['66', { iso2: 'TH', name: 'Thailand', flag: '🇹🇭' }],
  ['81', { iso2: 'JP', name: 'Japan', flag: '🇯🇵' }],
  ['82', { iso2: 'KR', name: 'South Korea', flag: '🇰🇷' }],
  ['84', { iso2: 'VN', name: 'Vietnam', flag: '🇻🇳' }],
  ['86', { iso2: 'CN', name: 'China', flag: '🇨🇳' }],
  ['90', { iso2: 'TR', name: 'Turkey', flag: '🇹🇷' }],
  ['91', { iso2: 'IN', name: 'India', flag: '🇮🇳' }],
  ['92', { iso2: 'PK', name: 'Pakistan', flag: '🇵🇰' }],
  ['93', { iso2: 'AF', name: 'Afghanistan', flag: '🇦🇫' }],
  ['94', { iso2: 'LK', name: 'Sri Lanka', flag: '🇱🇰' }],
  ['95', { iso2: 'MM', name: 'Myanmar', flag: '🇲🇲' }],
  ['98', { iso2: 'IR', name: 'Iran', flag: '🇮🇷' }],
  // 1-digit
  ['1', { iso2: 'US', name: 'USA/Canada', flag: '🇺🇸' }],
  ['7', { iso2: 'RU', name: 'Russia/KZ', flag: '🇷🇺' }],
];

// Sort longest first so 4-digit (e.g. "1242") matches before 1-digit ("1").
const SORTED_CODES = [...CODES].sort((a, b) => b[0].length - a[0].length);

/**
 * Strip non-digits from a phone string and return the leading digits.
 * Keeps the leading "+" implied (drops it).
 */
function digitsOnly(phone: string): string {
  return phone.replace(/\D/g, '');
}

/**
 * Look up country info for a phone number by greedy longest-prefix match.
 * Returns null if no match (empty phone, malformed, or unrecognized country code).
 */
export function phoneCountry(phone: string | null | undefined): CountryInfo | null {
  if (!phone) return null;
  const digits = digitsOnly(phone);
  if (!digits) return null;
  for (const [code, info] of SORTED_CODES) {
    if (digits.startsWith(code)) return info;
  }
  return null;
}

/**
 * Derive a flag emoji from an ISO-3166-1 alpha-2 country code (e.g. "AE" →
 * 🇦🇪) using Unicode regional-indicator symbols. Works for any valid code
 * without a lookup table. Returns null for missing/malformed input. Used where
 * we only have the customer's stored countryCode and no phone to infer from.
 */
export function flagFromIso2(iso2: string | null | undefined): string | null {
  if (!iso2 || !/^[A-Za-z]{2}$/.test(iso2)) return null;
  const cc = iso2.toUpperCase();
  return String.fromCodePoint(...[...cc].map((ch) => 127397 + ch.charCodeAt(0)));
}

/**
 * Format a phone number for display: keep the "+", insert a space after the
 * country code, leave the rest as-is. Tolerant of already-formatted input.
 */
export function formatPhoneDisplay(phone: string | null | undefined): string {
  if (!phone) return '';
  const trimmed = phone.trim();
  // Already starts with "+"? leave as-is
  if (trimmed.startsWith('+')) return trimmed;
  // Otherwise add "+" if it looks like an E.164 number
  const digits = digitsOnly(trimmed);
  if (!digits) return trimmed;
  return `+${digits}`;
}
